import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { ListingCard } from "../components/ListingCard";
import { SlidersHorizontal } from "lucide-react";
import { useI18n } from "../context/I18nContext";

const HERO_GEN =
  "/media/riviera-background.jpeg";
const CAT_BG = {
  cars: HERO_GEN,
  yachts: "https://images.unsplash.com/photo-1597609490757-5fc92ead5cb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  properties: "https://images.unsplash.com/photo-1693934304978-22274e6a3276?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  all: HERO_GEN,
};

const brandKey = (item) =>
  item.category === "cars"
    ? item.specs?.brand
    : item.category === "yachts"
    ? item.specs?.builder
    : item.specs?.city;

export default function Catalog({ category = "all" }) {
  const [params] = useSearchParams();
  const { t, lang } = useI18n();
  const [items, setItems] = useState(null);
  const [brand, setBrand] = useState(() => params.get("brand") || "all");
  const [sort, setSort] = useState(() => params.get("sort") || "featured");

  const HEADERS = {
    cars: { title: t("catalog.hCarsT"), sub: t("catalog.hCarsS") },
    yachts: { title: t("catalog.hYachtsT"), sub: t("catalog.hYachtsS") },
    properties: { title: t("catalog.hPropT"), sub: t("catalog.hPropS") },
    all: { title: t("catalog.hAllT"), sub: t("catalog.hAllS") },
  };
  const BRAND_LABEL = { cars: t("catalog.brandCars"), yachts: t("catalog.brandYachts"), properties: t("catalog.brandProperties"), all: t("catalog.brandCars") };
  const header = HEADERS[category] || HEADERS.all;

  const search = params.toString();

  const load = useCallback(async () => {
    setItems(null);
    const sp = new URLSearchParams(search);
    const q = {};
    if (category !== "all") q.category = category;
    const destination = sp.get("destination");
    const start = sp.get("start");
    const end = sp.get("end");
    const guests = sp.get("guests");
    if (destination) q.destination = destination;
    if (start) q.start = start;
    if (end) q.end = end;
    if (guests) q.guests = guests;
    try {
      const { data } = await api.get("/listings", { params: q });
      setItems(data);
    } catch {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, search, lang]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setBrand(params.get("brand") || "all");
    setSort(params.get("sort") || "featured");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const activeFilters = [
    params.get("destination"),
    params.get("start") && params.get("end") ? `${params.get("start")} → ${params.get("end")}` : null,
    params.get("guests") ? `${params.get("guests")} pers.` : null,
  ].filter(Boolean);

  const brands = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map(brandKey).filter(Boolean))).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }, [items]);

  const displayed = useMemo(() => {
    if (!items) return [];
    let list = brand === "all" ? items : items.filter((i) => brandKey(i) === brand);
    list = [...list];
    const askAsc = (x) => (x.customerPrice > 0 ? x.customerPrice : Infinity);
    if (sort === "price_asc") list.sort((a, b) => askAsc(a) - askAsc(b));
    else if (sort === "price_desc") list.sort((a, b) => (b.customerPrice || 0) - (a.customerPrice || 0));
    return list;
  }, [items, brand, sort]);

  const selectCls =
    "bg-[#0E0D0A]/60 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-[#F5F0E1] outline-none focus:border-[#D4AF37] [color-scheme:dark] transition-colors";

  return (
    <div className="min-h-screen pt-[72px]">
      <div className="relative py-16 sm:py-24 lg:py-28 border-b border-[#D4AF37]/10 overflow-hidden">
        <img src={CAT_BG[category] || CAT_BG.all} alt="" className="absolute inset-0 w-full h-full object-cover blur-[3px] scale-110" />
        <div className="absolute inset-0 bg-[#05060A]/72" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05060A]/55 via-[#05060A]/40 to-[#05060A]" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12">
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] tracking-luxe uppercase text-[#E5C378] mb-4">
            GROZNY LOCATION · Côte d'Azur
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E1] drop-shadow-lg">
            {header.title}
          </motion.h1>
          <p className="text-[#D8DDD7] mt-4 max-w-2xl drop-shadow">{header.sub}</p>
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-6">
              <SlidersHorizontal className="w-4 h-4 text-[#D4AF37]" />
              {activeFilters.map((f, i) => (
                <span key={i} className="text-xs font-mono2 text-[#C7D0CA] bg-black/30 border border-white/10 rounded-full px-3 py-1">{f}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-14">
        {items === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-3xl bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-display text-3xl text-[#F5F0E1] mb-3">{t("catalog.noResultsTitle")}</p>
            <p className="text-[#93A099]">{t("catalog.noResultsSub")}</p>
          </div>
        ) : (
          <>
            <div className="gz-card rounded-3xl p-4 sm:p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="catalog-filters">
              <div className="flex items-center gap-2 text-[#D4AF37] text-[11px] tracking-luxe uppercase shrink-0">
                <SlidersHorizontal className="w-4 h-4" /> {t("catalog.filter")}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-1 sm:justify-end w-full">
                <label className="flex flex-col gap-1 w-full sm:w-56">
                  <span className="text-[10px] uppercase tracking-widest text-[#7E8C84]">{BRAND_LABEL[category]}</span>
                  <select data-testid="filter-brand" value={brand} onChange={(e) => setBrand(e.target.value)} className={selectCls}>
                    <option value="all" className="bg-[#0E0D0A]">{t("catalog.all")}</option>
                    {brands.map((b) => (
                      <option key={b} value={b} className="bg-[#0E0D0A]">{b}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 w-full sm:w-56">
                  <span className="text-[10px] uppercase tracking-widest text-[#7E8C84]">{t("catalog.sortBy")}</span>
                  <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
                    <option value="featured" className="bg-[#0E0D0A]">{t("catalog.featured")}</option>
                    <option value="price_asc" className="bg-[#0E0D0A]">{t("catalog.priceAsc")}</option>
                    <option value="price_desc" className="bg-[#0E0D0A]">{t("catalog.priceDesc")}</option>
                  </select>
                </label>
              </div>
            </div>
            <p className="text-[#7E8C84] text-sm mb-8" data-testid="catalog-count">
              {displayed.length} {t(`categoryLabel.${category}`)} {brand !== "all" ? `· ${brand}` : t("catalog.disponibles")}
            </p>
            {displayed.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[#93A099]">{t("catalog.noFilterMatch")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="catalog-grid">
                {displayed.map((item, i) => (
                  <ListingCard key={item.id} item={item} index={i} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
